import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GoPlusCircle } from "react-icons/go";
import { FiMinusCircle } from "react-icons/fi";
import {
  calculateNights,
  calculateRowGst,
  numberToWords,
} from "./invoiceUtils";

const API_BASE = "https://demo.zorbastays.com/web-backend/billing";

const emptyRow = () => ({
  particulars: "",
  checkin: "",
  checkout: "",
  nights: "",
  rate: "",
  cgst: "",
  sgst: "",
});

// invoiceid: the invoice to load and edit (falls back to the :invoiceid route param
// so this works whether it's passed as a prop or rendered straight from a route)
// onUpdate: optional callback fired with the server response after a successful save
const InvoiceEdit = ({ invoiceid: invoiceidProp, onUpdate }) => {
  const navigate = useNavigate();
  const { invoiceid: invoiceidParam } = useParams();
  const invoiceid = invoiceidProp || invoiceidParam;

  const [rows, setRows] = useState([emptyRow()]);
  const [invoiceno, setinvoiceno] = useState("");
  const [invoicedate, setinvoicedate] = useState("");
  const [guestname, setguestname] = useState("");
  const [amountinwords, setamountinwords] = useState("");
  const [subtotal, setSubtotal] = useState("");
  const [gst, setGst] = useState("");
  const [total, setTotal] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFocus = (e) => (e.target.type = "date");
  const handleBlur = (e) => (e.target.type = "text");

  // ---------------- Load existing invoice ----------------
  useEffect(() => {
    if (!invoiceid) {
      setIsLoading(false);
      return;
    }

    const fetchInvoice = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const res = await fetch(`${API_BASE}/invoice/${invoiceid}`);
        const data = await res.json();

        if (!res.ok || !data.success) {
          setLoadError(data.message || "Failed to load invoice.");
          return;
        }

        const inv = data.invoice;
        setinvoiceno(inv.invoiceno || "");
        setinvoicedate(
          inv.invoicedate ? String(inv.invoicedate).split("T")[0] : "",
        );
        setguestname(inv.guestname || "");
        setamountinwords(inv.amountinwords || "");
        setSubtotal(inv.subtotal ?? "");
        setGst(inv.gst ?? "");
        setTotal(inv.total ?? "");
        setRows(
          inv.items && inv.items.length > 0
            ? inv.items.map((item) => ({
                particulars: item.particulars || "",
                checkin: item.checkin ? String(item.checkin).split("T")[0] : "",
                checkout: item.checkout
                  ? String(item.checkout).split("T")[0]
                  : "",
                nights: item.nights ?? "",
                rate: item.rate ?? "",
                cgst: item.cgst ?? "",
                sgst: item.sgst ?? "",
              }))
            : [emptyRow()],
        );
      } catch (err) {
        console.error("Error loading invoice:", err);
        setLoadError("Something went wrong while loading the invoice.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoice();
  }, [invoiceid]);

  const addRow = () => {
    if (rows.length < 6) {
      setRows([...rows, emptyRow()]);
    }
  };

  const removeRow = (indexToRemove) => {
    setRows(rows.filter((_, index) => index !== indexToRemove));
  };

  const updateRow = (index, field, value) => {
    setRows((prevRows) =>
      prevRows.map((row, i) => {
        if (i !== index) return row;

        let updated = { ...row, [field]: value };

        // Auto-calculate nights whenever either date changes
        if (field === "checkin" || field === "checkout") {
          const nights = calculateNights(updated.checkin, updated.checkout);
          if (nights !== null) {
            updated.nights = nights;
          }
        }

        // Auto-calculate CGST/SGST (9% each) whenever rate or nights changes
        if (
          field === "rate" ||
          field === "nights" ||
          field === "checkin" ||
          field === "checkout"
        ) {
          const gst = calculateRowGst(updated.rate, updated.nights);
          if (gst) {
            updated.cgst = gst.cgst;
            updated.sgst = gst.sgst;
          }
        }

        return updated;
      }),
    );
  };

  // Auto-calculate Sub Total, GST and Total whenever any row changes.
  // (Still plain editable inputs below — this just keeps them in sync
  // automatically; you can still overwrite them by hand if needed.)
  useEffect(() => {
    const newSubtotal = rows.reduce(
      (sum, r) => sum + (parseFloat(r.rate) || 0) * (parseFloat(r.nights) || 0),
      0,
    );
    const newGst = rows.reduce(
      (sum, r) => sum + (parseFloat(r.cgst) || 0) + (parseFloat(r.sgst) || 0),
      0,
    );
    const newTotal = newSubtotal + newGst;

    setSubtotal(newSubtotal ? newSubtotal.toFixed(2) : "");
    setGst(newGst ? newGst.toFixed(2) : "");
    setTotal(newTotal ? newTotal.toFixed(2) : "");
  }, [rows]);

  // Keeps "In Words" in sync with Total, whether Total changed automatically
  // (from the rows effect above) or was typed in manually.
  useEffect(() => {
    const numericTotal = parseFloat(total) || 0;
    setamountinwords(numericTotal ? numberToWords(numericTotal) : "");
  }, [total]);

  const handleCancel = () => {
    const confirmed = window.confirm("Sure want to discard changes?");
    if (confirmed) {
      navigate("/Bills");
    }
  };

  const handleUpdate = async () => {
    if (!invoiceid) {
      alert("No invoice selected to update.");
      return;
    }
    if (!invoiceno || !invoicedate || !guestname) {
      alert("Invoice No, Invoice Date and Guest Name are required.");
      return;
    }
    if (rows.length === 0) {
      alert("Invoice must contain at least one item.");
      return;
    }

    const payload = {
      invoiceno,
      invoicedate,
      guestname,
      amountinwords,
      subtotal: subtotal || 0,
      gst: gst || 0,
      total: total || 0,
      items: rows.map((row) => ({
        particulars: row.particulars,
        checkin: row.checkin || null,
        checkout: row.checkout || null,
        nights: row.nights || 0,
        rate: row.rate || 0,
        cgst: row.cgst || 0,
        sgst: row.sgst || 0,
      })),
    };

    try {
      setIsSubmitting(true);
      const res = await fetch(`${API_BASE}/invoice/${invoiceid}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        alert(data.message || "Failed to update invoice.");
        return;
      }

      alert("Invoice updated successfully!");
      if (onUpdate) onUpdate(data);
      navigate("/bills");
    } catch (err) {
      console.error("Error updating invoice:", err);
      alert("Something went wrong while updating the invoice.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto p-10 text-center text-gray-600">
        Loading invoice...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-5xl mx-auto p-10 text-center text-red-600">
        {loadError}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-10 bg-white text-gray-800 font-sans print:p-0">
      <style>{`
        @media print {
          @page { margin: 0; }
          body { margin: 1.6cm; }
          .no-print { display: none; }
        }
      `}</style>

      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-4xl font-bold mb-2">Invoice</h1>
          <br />
          <br />
          <h1 className="nt-seawave text-4xl mb-2 text-black">ZORBA STAYS</h1>
          <p className="text-sm">
            306, 7 - Business Centrum, <br /> Amin Marg, Rajkot, 360001
          </p>
          <p className="text-sm">GSTIN No: 24AADFZ2976K1ZO</p>
        </div>
        <div className="w-24 h-24">
          <img
            src="https://zorbastays.com/android-chrome-512x512.png"
            alt="Zorba Stays Logo"
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      <div className="border-t border-black mb-6"></div>

      {/* Invoice Meta */}
      <div className="flex gap-16 mb-8">
        <div className="flex flex-col gap-1">
          <p className="text-xs font-bold uppercase">Invoice No.</p>
          <input
            type="text"
            value={invoiceno}
            onChange={(e) => setinvoiceno(e.target.value)}
            className="w-20 p-0 border-0 bg-transparent outline-none focus:ring-0"
          />
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-xs font-bold uppercase m-0">Invoice Date</p>
          <input
            type="date"
            value={invoicedate}
            onChange={(e) => setinvoicedate(e.target.value)}
            className="p-0 border-0 bg-transparent outline-none focus:ring-0"
            // onFocus={handleFocus}
            // onBlur={handleBlur}
          />
        </div>
      </div>

      <div className="mb-8">
        <p className="font-bold">Invoice To:-</p>
        <input
          type="text"
          value={guestname}
          onChange={(e) => setguestname(e.target.value)}
          className="w-full text-lg p-0 leading-tight border-0 bg-transparent outline-none focus:ring-0"
        />
      </div>

      {/* Table Design */}
      <table className="w-full border-collapse border-t border-b border-black text-center text-sm">
        <thead>
          <tr className="uppercase text-xs font-bold border-b border-black">
            <th className="py-3  border-black w-[60px]">Sr. No.</th>
            <th className="py-3">Particulars</th>
            <th className="py-3">Period of stay</th>
            <th className="py-3">No. Nights</th>
            <th className="py-3">Per Night Rate</th>
            <th className="py-3">CGST</th>
            <th className="py-3">SGST</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-b border-black">
              <td className="py-3  border-black">{index + 1}</td>
              <td className="py-3 px-2">
                <input
                  className="w-full text-center outline-none"
                  value={row.particulars}
                  onChange={(e) =>
                    updateRow(index, "particulars", e.target.value)
                  }
                />
              </td>
              <td className="py-3 px-2 flex justify-center items-center gap-1">
                <input
                  type="date"
                  className="w-full text-center  outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
                  value={row.checkin}
                  onChange={(e) => updateRow(index, "checkin", e.target.value)}
                  // onFocus={handleFocus}
                  // onBlur={handleBlur}
                  placeholder="DD-MM-YYYY"
                />
                <span>to</span>
                <input
                  type="date"
                  className="w-full text-center  outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
                  value={row.checkout}
                  onChange={(e) => updateRow(index, "checkout", e.target.value)}
                  // onFocus={handleFocus}
                  // onBlur={handleBlur}
                  placeholder="DD-MM-YYYY"
                />
              </td>
              <td className="py-3">
                <input
                  className="w-full text-center outline-none"
                  value={row.nights}
                  onChange={(e) => updateRow(index, "nights", e.target.value)}
                />
              </td>
              <td className="py-3">
                <input
                  className="w-full text-center outline-none"
                  value={row.rate}
                  onChange={(e) => updateRow(index, "rate", e.target.value)}
                />
              </td>
              <td className="py-3">
                <input
                  className="w-full text-center outline-none"
                  value={row.cgst}
                  onChange={(e) => updateRow(index, "cgst", e.target.value)}
                />
              </td>
              <td className="py-3">
                <input
                  className="w-full text-center outline-none"
                  value={row.sgst}
                  onChange={(e) => updateRow(index, "sgst", e.target.value)}
                />
              </td>
              <td className="py-3 no-print">
                {index >= 1 && (
                  <button
                    onClick={() => removeRow(index)}
                    className="px-2 py-1 hover:scale-110"
                  >
                    <FiMinusCircle size={18} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Add Button */}
      <div className="my-2 no-print">
        <button
          onClick={addRow}
          disabled={rows.length >= 6}
          className={`px-4 py-2 ${rows.length >= 6 ? "opacity-50 cursor-not-allowed" : "hover:scale-110"}`}
        >
          <GoPlusCircle size={22} />
        </button>
      </div>

      {/* Totals (Design preserved) */}
      <div className="flex justify-between items-start mt-6 px-2">
        <div className="w-1/2">
          <p className="font-bold mb-1">In Words:</p>
          <input
            type="text"
            value={amountinwords}
            onChange={(e) => setamountinwords(e.target.value)}
            className="
    w-full
    uppercase
    p-0
    m-0
    h-7
    leading-none
    border-0
    border-b
    border-black
    bg-transparent
    rounded-none
    focus:ring-0
    focus:border-black
  "
          />
        </div>
        <div className="w-72">
          <div className="flex justify-between py-1">
            <span className="font-bold">Sub Total</span>
            <input
              type="text"
              value={subtotal}
              onChange={(e) => setSubtotal(e.target.value)}
              className="w-32 text-right  outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
            />
          </div>
          <div className="flex justify-between py-1 border-t border-black">
            <span className="font-bold">GST</span>
            <input
              type="text"
              value={gst}
              onChange={(e) => setGst(e.target.value)}
              className="w-32 text-right  outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
            />
          </div>
          <div className="flex justify-between py-2 font-bold border-t border-black mt-1">
            <span>Total</span>
            <input
              type="text"
              value={total}
              onChange={(e) => setTotal(e.target.value)}
              className="w-32 text-right  outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
            />
          </div>
          <div className="flex justify-between py-2 font-bold border-t border-black"></div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-20 text-center text-xs border-t border-black pt-8">
        <p>www.zorbastays.com | Contact: +91 63552 31066, +91 98750 28066</p>
      </div>

      {/* Update/Cancel Buttons */}
      <div className="flex justify-center gap-4 mt-8 mb-10 no-print">
        <button
          onClick={handleUpdate}
          disabled={isSubmitting}
          className={`px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm ${
            isSubmitting ? "opacity-60 cursor-not-allowed" : ""
          }`}
        >
          {isSubmitting ? "Updating..." : "Update"}
        </button>
        <button
          onClick={handleCancel}
          className="px-6 z-30 py-2 bg-gray-800 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-gray-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default InvoiceEdit;
//working code
// import React, { useState, useEffect } from "react";
// import { useParams } from "react-router-dom";
// import { GoPlusCircle } from "react-icons/go";
// import { FiMinusCircle } from "react-icons/fi";

// const API_BASE = "https://demo.zorbastays.com/web-backend/billing";

// const emptyRow = () => ({
//   particulars: "",
//   checkin: "",
//   checkout: "",
//   nights: "",
//   rate: "",
//   cgst: "",
//   sgst: "",
// });

// // invoiceid: the invoice to load and edit (falls back to the :invoiceid route param
// // so this works whether it's passed as a prop or rendered straight from a route)
// // onUpdate: optional callback fired with the server response after a successful save
// const InvoiceEdit = ({ invoiceid: invoiceidProp, onUpdate }) => {
//   const { invoiceid: invoiceidParam } = useParams();
//   const invoiceid = invoiceidProp || invoiceidParam;

//   const [rows, setRows] = useState([emptyRow()]);
//   const [invoiceno, setinvoiceno] = useState("");
//   const [invoicedate, setinvoicedate] = useState("");
//   const [guestname, setguestname] = useState("");
//   const [amountinwords, setamountinwords] = useState("");
//   const [subtotal, setSubtotal] = useState("");
//   const [gst, setGst] = useState("");
//   const [total, setTotal] = useState("");

//   const [isLoading, setIsLoading] = useState(true);
//   const [loadError, setLoadError] = useState(null);
//   const [isSubmitting, setIsSubmitting] = useState(false);

//   const handleFocus = (e) => (e.target.type = "date");
//   const handleBlur = (e) => (e.target.type = "text");

//   // ---------------- Load existing invoice ----------------
//   useEffect(() => {
//     if (!invoiceid) {
//       setIsLoading(false);
//       return;
//     }

//     const fetchInvoice = async () => {
//       try {
//         setIsLoading(true);
//         setLoadError(null);
//         const res = await fetch(`${API_BASE}/invoice/${invoiceid}`);
//         const data = await res.json();

//         if (!res.ok || !data.success) {
//           setLoadError(data.message || "Failed to load invoice.");
//           return;
//         }

//         const inv = data.invoice;
//         setinvoiceno(inv.invoiceno || "");
//         setinvoicedate(
//           inv.invoicedate ? String(inv.invoicedate).split("T")[0] : "",
//         );
//         setguestname(inv.guestname || "");
//         setamountinwords(inv.amountinwords || "");
//         setSubtotal(inv.subtotal ?? "");
//         setGst(inv.gst ?? "");
//         setTotal(inv.total ?? "");
//         setRows(
//           inv.items && inv.items.length > 0
//             ? inv.items.map((item) => ({
//                 particulars: item.particulars || "",
//                 checkin: item.checkin ? String(item.checkin).split("T")[0] : "",
//                 checkout: item.checkout
//                   ? String(item.checkout).split("T")[0]
//                   : "",
//                 nights: item.nights ?? "",
//                 rate: item.rate ?? "",
//                 cgst: item.cgst ?? "",
//                 sgst: item.sgst ?? "",
//               }))
//             : [emptyRow()],
//         );
//       } catch (err) {
//         console.error("Error loading invoice:", err);
//         setLoadError("Something went wrong while loading the invoice.");
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchInvoice();
//   }, [invoiceid]);

//   const addRow = () => {
//     if (rows.length < 6) {
//       setRows([...rows, emptyRow()]);
//     }
//   };

//   const removeRow = (indexToRemove) => {
//     setRows(rows.filter((_, index) => index !== indexToRemove));
//   };

//   const updateRow = (index, field, value) => {
//     setRows((prevRows) =>
//       prevRows.map((row, i) =>
//         i === index ? { ...row, [field]: value } : row,
//       ),
//     );
//   };

//   const handleUpdate = async () => {
//     if (!invoiceid) {
//       alert("No invoice selected to update.");
//       return;
//     }
//     if (!invoiceno || !invoicedate || !guestname) {
//       alert("Invoice No, Invoice Date and Guest Name are required.");
//       return;
//     }
//     if (rows.length === 0) {
//       alert("Invoice must contain at least one item.");
//       return;
//     }

//     const payload = {
//       invoiceno,
//       invoicedate,
//       guestname,
//       amountinwords,
//       subtotal: subtotal || 0,
//       gst: gst || 0,
//       total: total || 0,
//       items: rows.map((row) => ({
//         particulars: row.particulars,
//         checkin: row.checkin || null,
//         checkout: row.checkout || null,
//         nights: row.nights || 0,
//         rate: row.rate || 0,
//         cgst: row.cgst || 0,
//         sgst: row.sgst || 0,
//       })),
//     };

//     try {
//       setIsSubmitting(true);
//       const res = await fetch(`${API_BASE}/invoice/${invoiceid}`, {
//         method: "PUT",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(payload),
//       });

//       const data = await res.json();

//       if (!res.ok || !data.success) {
//         alert(data.message || "Failed to update invoice.");
//         return;
//       }

//       alert("Invoice updated successfully!");
//       if (onUpdate) onUpdate(data);
//     } catch (err) {
//       console.error("Error updating invoice:", err);
//       alert("Something went wrong while updating the invoice.");
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   if (isLoading) {
//     return (
//       <div className="max-w-5xl mx-auto p-10 text-center text-gray-600">
//         Loading invoice...
//       </div>
//     );
//   }

//   if (loadError) {
//     return (
//       <div className="max-w-5xl mx-auto p-10 text-center text-red-600">
//         {loadError}
//       </div>
//     );
//   }

//   return (
//     <div className="max-w-5xl mx-auto p-10 bg-white text-gray-800 font-sans print:p-0">
//       <style>{`
//         @media print {
//           @page { margin: 0; }
//           body { margin: 1.6cm; }
//           .no-print { display: none; }
//         }
//       `}</style>

//       {/* Header */}
//       <div className="flex justify-between items-start mb-6">
//         <div>
//           <h1 className="text-4xl font-bold mb-2">Invoice</h1>
//           <div className="w-48 mb-2">
//             <img
//               src="https://imagedelivery.net/JqAydcRLXyliJTMOjPllJQ/7c57c4a2-564b-4b83-62b0-761f834b5c00/public"
//               alt="Zorba Stays"
//               className="w-full h-auto object-contain"
//             />
//           </div>
//           <p className="text-sm">
//             306, 7 - Business Centrum, <br /> Amin Marg, Rajkot, 360001
//           </p>
//           <p className="text-sm">GSTIN No: 24AADFZ2976K1ZO</p>
//         </div>
//         <div className="w-24 h-24">
//           <img
//             src="https://zorbastays.com/android-chrome-512x512.png"
//             alt="Zorba Stays Logo"
//             className="w-full h-full object-contain"
//           />
//         </div>
//       </div>

//       <div className="border-t border-black mb-6"></div>

//       {/* Invoice Meta */}
//       <div className="flex gap-16 mb-8">
//         <div>
//           <p className="text-xs font-bold uppercase">Invoice No.</p>
//           <input
//             type="text"
//             value={invoiceno}
//             onChange={(e) => setinvoiceno(e.target.value)}
//             className="w-20 outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//           />
//         </div>
//         <div>
//           <p className="text-xs font-bold uppercase">Invoice Date</p>
//           <input
//             type="text"
//             value={invoicedate}
//             onChange={(e) => setinvoicedate(e.target.value)}
//             className="outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//             onFocus={handleFocus}
//             onBlur={handleBlur}
//           />
//         </div>
//       </div>

//       <div className="mb-8">
//         <p className="font-bold">Invoice To:-</p>
//         <input
//           type="text"
//           value={guestname}
//           onChange={(e) => setguestname(e.target.value)}
//           className="w-full text-lg outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//         />
//       </div>

//       {/* Table Design */}
//       <table className="w-full border-collapse border-t border-b border-black text-center text-sm">
//         <thead>
//           <tr className="uppercase text-xs font-bold border-b border-black">
//             <th className="py-3 border-r border-black w-[60px]">Sr. No.</th>
//             <th className="py-3">Particulars</th>
//             <th className="py-3">Period of stay</th>
//             <th className="py-3">No. Nights</th>
//             <th className="py-3">Per Night Rate</th>
//             <th className="py-3">CGST</th>
//             <th className="py-3">SGST</th>
//           </tr>
//         </thead>
//         <tbody>
//           {rows.map((row, index) => (
//             <tr key={index} className="border-b border-black">
//               <td className="py-3 border-r border-black">{index + 1}</td>
//               <td className="py-3 px-2">
//                 <input
//                   className="w-full text-center outline-none"
//                   value={row.particulars}
//                   onChange={(e) =>
//                     updateRow(index, "particulars", e.target.value)
//                   }
//                 />
//               </td>
//               <td className="py-3 px-2 flex justify-center items-center gap-1">
//                 <input
//                   type="text"
//                   className="w-full text-center outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//                   value={row.checkin}
//                   onChange={(e) => updateRow(index, "checkin", e.target.value)}
//                   onFocus={handleFocus}
//                   onBlur={handleBlur}
//                   placeholder="DD-MM-YYYY"
//                 />
//                 <span>to</span>
//                 <input
//                   type="text"
//                   className="w-full text-center outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//                   value={row.checkout}
//                   onChange={(e) => updateRow(index, "checkout", e.target.value)}
//                   onFocus={handleFocus}
//                   onBlur={handleBlur}
//                   placeholder="DD-MM-YYYY"
//                 />
//               </td>
//               <td className="py-3">
//                 <input
//                   className="w-full text-center outline-none"
//                   value={row.nights}
//                   onChange={(e) => updateRow(index, "nights", e.target.value)}
//                 />
//               </td>
//               <td className="py-3">
//                 <input
//                   className="w-full text-center outline-none"
//                   value={row.rate}
//                   onChange={(e) => updateRow(index, "rate", e.target.value)}
//                 />
//               </td>
//               <td className="py-3">
//                 <input
//                   className="w-full text-center outline-none"
//                   value={row.cgst}
//                   onChange={(e) => updateRow(index, "cgst", e.target.value)}
//                 />
//               </td>
//               <td className="py-3">
//                 <input
//                   className="w-full text-center outline-none"
//                   value={row.sgst}
//                   onChange={(e) => updateRow(index, "sgst", e.target.value)}
//                 />
//               </td>
//               <td className="py-3 no-print">
//                 {index >= 1 && (
//                   <button
//                     onClick={() => removeRow(index)}
//                     className="px-2 py-1 hover:scale-110"
//                   >
//                     <FiMinusCircle size={18} />
//                   </button>
//                 )}
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </table>

//       {/* Add Button */}
//       <div className="my-2 no-print">
//         <button
//           onClick={addRow}
//           disabled={rows.length >= 6}
//           className={`px-4 py-2 ${rows.length >= 6 ? "opacity-50 cursor-not-allowed" : "hover:scale-110"}`}
//         >
//           <GoPlusCircle size={22} />
//         </button>
//       </div>

//       {/* Totals (Design preserved) */}
//       <div className="flex justify-between items-start mt-6 px-2">
//         <div className="w-1/2">
//           <p className="font-bold mb-1">In Words:</p>
//           <input
//             type="text"
//             value={amountinwords}
//             onChange={(e) => setamountinwords(e.target.value)}
//             className="w-full uppercase outline-none border-t-0 border-l-0 border-r-0 border-b border-black focus:ring-0 focus:border-black"
//           />
//         </div>
//         <div className="w-64">
//           <div className="flex justify-between py-1">
//             <span className="font-bold">Sub Total</span>
//             <input
//               type="text"
//               value={subtotal}
//               onChange={(e) => setSubtotal(e.target.value)}
//               className="w-24 text-right outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//             />
//           </div>
//           <div className="flex justify-between py-1 border-t border-black">
//             <span className="font-bold">GST</span>
//             <input
//               type="text"
//               value={gst}
//               onChange={(e) => setGst(e.target.value)}
//               className="w-24 text-right outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//             />
//           </div>
//           <div className="flex justify-between py-2 font-bold border-t border-black mt-1">
//             <span>Total</span>
//             <input
//               type="text"
//               value={total}
//               onChange={(e) => setTotal(e.target.value)}
//               className="w-24 text-right outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//             />
//           </div>
//           <div className="flex justify-between py-2 font-bold border-t border-black"></div>
//         </div>
//       </div>

//       {/* Footer */}
//       <div className="mt-20 text-center text-xs border-t border-black pt-8">
//         <p>www.zorbastays.com | Contact: +91 63552 31066, +91 98750 28066</p>
//       </div>

//       {/* Update/Cancel Buttons */}
//       <div className="flex justify-center gap-4 mt-8 mb-10 no-print">
//         <button
//           onClick={handleUpdate}
//           disabled={isSubmitting}
//           className={`px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm ${
//             isSubmitting ? "opacity-60 cursor-not-allowed" : ""
//           }`}
//         >
//           {isSubmitting ? "Updating..." : "Update"}
//         </button>
//         <button
//           onClick={() => window.location.reload()}
//           className="px-6 z-30 py-2 bg-gray-800 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-gray-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
//         >
//           Cancel
//         </button>
//       </div>
//     </div>
//   );
// };

// export default InvoiceEdit;
